import {
  Bookmark,
  BookmarkBorder,
  Business,
  Check,
  Close,
  FirstPage,
  LastPage,
  NavigateBefore,
  NavigateNext,
  PersonSearch,
  Search,
} from "@mui/icons-material";
import ApolloIcon from "../../../components/icons/ApolloIcon";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import LoadingButton from "../../../components/LoadingButton";
import { hostname, parseError } from "../../../utils";

// Saved search type
interface ApolloSavedSearch {
  id: string;
  name: string;
  searchParams: Record<string, any>;
  lastResultCount?: string;
  created_date: string;
  updated_date: string;
}

interface ApolloSearchDialogProps {
  open: boolean;
  onClose: () => void;
  getToken: () => Promise<string>;
  eventListener: (event: any) => void;
  companyId: string;
  listId: string;
  onSuccess?: () => void;
  onBackgroundTaskCreated?: () => void;
}

// Person type from Apollo API response
// Note: Search endpoint returns has_* booleans, not actual values
// Actual values are only revealed on enrichment
interface ApolloPerson {
  id: string;
  first_name: string;
  last_name?: string;
  last_name_obfuscated?: string;
  title: string;
  headline?: string;
  // These are only available after enrichment
  city?: string;
  state?: string;
  country?: string;
  linkedin_url?: string;
  // Search endpoint returns these as availability indicators
  has_email?: boolean;
  has_city?: boolean;
  has_state?: boolean;
  has_country?: boolean;
  has_direct_phone?: string;
  organization?: {
    name: string;
    website_url?: string;
    has_city?: boolean;
    has_state?: boolean;
    has_country?: boolean;
    has_industry?: boolean;
    has_phone?: boolean;
  };
}

// Seniority Level Options
const seniorityOptions = [
  { value: "owner", label: "Owner" },
  { value: "founder", label: "Founder" },
  { value: "c_suite", label: "C-Suite" },
  { value: "partner", label: "Partner" },
  { value: "vp", label: "VP" },
  { value: "head", label: "Head" },
  { value: "director", label: "Director" },
  { value: "manager", label: "Manager" },
  { value: "senior", label: "Senior" },
  { value: "entry", label: "Entry" },
  { value: "intern", label: "Intern" },
];

// Department Options
const departmentOptions = [
  { value: "engineering_technical", label: "Engineering & Technical" },
  { value: "sales", label: "Sales" },
  { value: "marketing", label: "Marketing" },
  { value: "finance", label: "Finance" },
  { value: "human_resources", label: "Human Resources" },
  { value: "operations", label: "Operations" },
  { value: "information_technology", label: "Information Technology" },
  { value: "legal", label: "Legal" },
  { value: "product_management", label: "Product Management" },
  { value: "design", label: "Design" },
  { value: "support", label: "Support" },
  { value: "education", label: "Education" },
  { value: "consulting", label: "Consulting" },
  { value: "accounting", label: "Accounting" },
  { value: "arts_and_design", label: "Arts & Design" },
  { value: "administrative", label: "Administrative" },
];

// Employee Count Ranges
const employeeRangeOptions = [
  { value: "1,10", label: "1-10" },
  { value: "11,50", label: "11-50" },
  { value: "51,200", label: "51-200" },
  { value: "201,500", label: "201-500" },
  { value: "501,1000", label: "501-1,000" },
  { value: "1001,5000", label: "1,001-5,000" },
  { value: "5001,10000", label: "5,001-10,000" },
  { value: "10001,", label: "10,001+" },
];

// Revenue Range Options
const revenueRangeOptions = [
  { value: "0,1000000", label: "Under $1M" },
  { value: "1000000,10000000", label: "$1M - $10M" },
  { value: "10000000,50000000", label: "$10M - $50M" },
  { value: "50000000,100000000", label: "$50M - $100M" },
  { value: "100000000,500000000", label: "$100M - $500M" },
  { value: "500000000,1000000000", label: "$500M - $1B" },
  { value: "1000000000,", label: "$1B+" },
];

// Email Status Options
const emailStatusOptions = [
  { value: "verified", label: "Verified" },
  { value: "guessed", label: "Guessed" },
  { value: "unavailable", label: "Unavailable" },
];

// Common locations for autocomplete
const commonLocations = [
  "United States",
  "California, US",
  "New York, US",
  "Texas, US",
  "Florida, US",
  "Illinois, US",
  "Washington, US",
  "Massachusetts, US",
  "Colorado, US",
  "Georgia, US",
  "United Kingdom",
  "London, United Kingdom",
  "Canada",
  "Toronto, Canada",
  "Germany",
  "France",
  "Australia",
  "India",
  "Singapore",
  "Japan",
];

// Industry keyword tags
const industryOptions = [
  "technology",
  "software",
  "saas",
  "fintech",
  "healthcare",
  "financial services",
  "banking",
  "e-commerce",
  "retail",
  "manufacturing",
  "consulting",
  "marketing",
  "advertising",
  "real estate",
  "education",
  "telecommunications",
  "media",
  "entertainment",
  "automotive",
  "energy",
  "cybersecurity",
  "ai",
  "machine learning",
  "cloud computing",
  "b2b",
  "b2c",
  "startup",
];

const ApolloSearchDialog: React.FC<ApolloSearchDialogProps> = ({
  open,
  onClose,
  getToken,
  eventListener,
  companyId,
  listId,
  onSuccess,
  onBackgroundTaskCreated,
}) => {
  // Loading states
  const [searchLoading, setSearchLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);

  // Results
  const [people, setPeople] = useState<ApolloPerson[]>([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [hasSearched, setHasSearched] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Status message
  const [statusMessage, setStatusMessage] = useState<{
    type: "success" | "error" | "info" | "warning";
    message: string;
  } | null>(null);

  // Saved searches state
  const [savedSearches, setSavedSearches] = useState<ApolloSavedSearch[]>([]);
  const [savedSearchesLoading, setSavedSearchesLoading] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveSearchName, setSaveSearchName] = useState("");
  const [savingSearch, setSavingSearch] = useState(false);
  const [savedListsExpanded, setSavedListsExpanded] = useState(true);

  // Person Filters
  const [personTitles, setPersonTitles] = useState<string[]>([]);
  const [personTitleInput, setPersonTitleInput] = useState("");
  const [personLocations, setPersonLocations] = useState<string[]>([]);
  const [personSeniorities, setPersonSeniorities] = useState<string[]>([]);
  const [personDepartments, setPersonDepartments] = useState<string[]>([]);

  // Organization Filters
  const [organizationLocations, setOrganizationLocations] = useState<string[]>(
    []
  );
  const [organizationDomains, setOrganizationDomains] = useState<string[]>([]);
  const [organizationDomainInput, setOrganizationDomainInput] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [organizationNumEmployeesRanges, setOrganizationNumEmployeesRanges] =
    useState<string[]>([]);
  const [organizationRevenueRanges, setOrganizationRevenueRanges] = useState<
    string[]
  >([]);
  const [organizationKeywordTags, setOrganizationKeywordTags] = useState<
    string[]
  >([]);

  // Other Filters
  const [qKeywords, setQKeywords] = useState("");
  const [contactEmailStatus, setContactEmailStatus] = useState<string[]>([]);
  const [perPage, setPerPage] = useState(25);

  const handleAddChip = (
    value: string,
    setter: React.Dispatch<React.SetStateAction<string[]>>,
    inputSetter: React.Dispatch<React.SetStateAction<string>>
  ) => {
    if (value.trim()) {
      setter((prev) => [...prev, value.trim()]);
      inputSetter("");
    }
  };

  const handleDeleteChip = (
    index: number,
    setter: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    setter((prev) => prev.filter((_, i) => i !== index));
  };

  // Load saved searches on mount
  const loadSavedSearches = async () => {
    setSavedSearchesLoading(true);
    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/apollo/saved-searches?companyId=${companyId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setSavedSearches(response.data.savedSearches || []);
    } catch (err) {
      console.error("Failed to load saved searches:", err);
    } finally {
      setSavedSearchesLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadSavedSearches();
    }
  }, [open, companyId]);

  // Save current search
  const handleSaveSearch = async () => {
    if (!saveSearchName.trim()) return;

    setSavingSearch(true);
    try {
      const token = await getToken();
      const searchParams = buildSearchParams();

      await axios.post(
        `${hostname}/apollo/saved-searches`,
        {
          companyId,
          name: saveSearchName.trim(),
          searchParams,
          lastResultCount: totalEntries,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setShowSaveDialog(false);
      setSaveSearchName("");
      loadSavedSearches();
      eventListener({
        type: "SET_SUCCESS",
        message: "Search saved successfully",
      });
    } catch (err) {
      setStatusMessage({
        type: "error",
        message: parseError(err) || "Failed to save search.",
      });
    } finally {
      setSavingSearch(false);
    }
  };

  // Delete a saved search
  const handleDeleteSavedSearch = async (searchId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const token = await getToken();
      await axios.delete(`${hostname}/apollo/saved-searches/${searchId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      loadSavedSearches();
    } catch (err) {
      setStatusMessage({
        type: "error",
        message: parseError(err) || "Failed to delete saved search.",
      });
    }
  };

  // Load a saved search into the filters
  const handleLoadSavedSearch = (savedSearch: ApolloSavedSearch) => {
    const params = savedSearch.searchParams;

    // Reset all filters first
    setPersonTitles(params.person_titles || []);
    setPersonLocations(params.person_locations || []);
    setPersonSeniorities(params.person_seniorities || []);
    setPersonDepartments(params.person_departments || []);
    setOrganizationLocations(params.organization_locations || []);
    setOrganizationDomains(params.q_organization_domains || []);
    setOrganizationName(params.q_organization_name || "");
    setOrganizationNumEmployeesRanges(params.organization_num_employees_ranges || []);
    setOrganizationRevenueRanges(params.organization_revenue_ranges || []);
    setOrganizationKeywordTags(params.q_organization_keyword_tags || []);
    setQKeywords(params.q_keywords || "");
    setContactEmailStatus(params.contact_email_status || []);

    // Clear results and run search
    setPeople([]);
    setTotalEntries(0);
    setSelectedIds(new Set());
    setHasSearched(false);
  };

  const handleSearch = async (page: number = 1, preserveSelections: boolean = false) => {
    setSearchLoading(true);
    setStatusMessage(null);
    if (!preserveSelections) {
      setSelectedIds(new Set());
    }
    setCurrentPage(page);

    try {
      const token = await getToken();

      // Build search params
      const searchParams: Record<string, any> = {
        per_page: perPage,
        page: page,
      };

      // Person filters
      if (personTitles.length > 0) searchParams.person_titles = personTitles;
      if (personLocations.length > 0)
        searchParams.person_locations = personLocations;
      if (personSeniorities.length > 0)
        searchParams.person_seniorities = personSeniorities;
      if (personDepartments.length > 0)
        searchParams.person_departments = personDepartments;

      // Organization filters
      if (organizationLocations.length > 0)
        searchParams.organization_locations = organizationLocations;
      if (organizationDomains.length > 0)
        searchParams.q_organization_domains = organizationDomains;
      if (organizationName.trim())
        searchParams.q_organization_name = organizationName.trim();
      if (organizationNumEmployeesRanges.length > 0)
        searchParams.organization_num_employees_ranges =
          organizationNumEmployeesRanges;
      if (organizationRevenueRanges.length > 0)
        searchParams.organization_revenue_ranges = organizationRevenueRanges;
      if (organizationKeywordTags.length > 0)
        searchParams.q_organization_keyword_tags = organizationKeywordTags;

      // Other filters
      if (qKeywords.trim()) searchParams.q_keywords = qKeywords.trim();
      if (contactEmailStatus.length > 0)
        searchParams.contact_email_status = contactEmailStatus;

      const response = await axios.post(
        `${hostname}/lists/${listId}/apollo/search`,
        {
          companyId,
          searchParams,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setPeople(response.data.people || []);
      setTotalEntries(response.data.pagination?.total_entries || 0);
      setHasSearched(true);

      if ((response.data.people || []).length === 0) {
        setStatusMessage({
          type: "warning",
          message:
            "No people found matching your criteria. Try adjusting your filters.",
        });
      }
    } catch (err) {
      setStatusMessage({
        type: "error",
        message: parseError(err) || "Failed to search Apollo.",
      });
    } finally {
      setSearchLoading(false);
    }
  };

  // Helper to build search params
  const buildSearchParams = (): Record<string, any> => {
    const searchParams: Record<string, any> = {};

    // Person filters
    if (personTitles.length > 0) searchParams.person_titles = personTitles;
    if (personLocations.length > 0)
      searchParams.person_locations = personLocations;
    if (personSeniorities.length > 0)
      searchParams.person_seniorities = personSeniorities;
    if (personDepartments.length > 0)
      searchParams.person_departments = personDepartments;

    // Organization filters
    if (organizationLocations.length > 0)
      searchParams.organization_locations = organizationLocations;
    if (organizationDomains.length > 0)
      searchParams.q_organization_domains = organizationDomains;
    if (organizationName.trim())
      searchParams.q_organization_name = organizationName.trim();
    if (organizationNumEmployeesRanges.length > 0)
      searchParams.organization_num_employees_ranges =
        organizationNumEmployeesRanges;
    if (organizationRevenueRanges.length > 0)
      searchParams.organization_revenue_ranges = organizationRevenueRanges;
    if (organizationKeywordTags.length > 0)
      searchParams.q_organization_keyword_tags = organizationKeywordTags;

    // Other filters
    if (qKeywords.trim()) searchParams.q_keywords = qKeywords.trim();
    if (contactEmailStatus.length > 0)
      searchParams.contact_email_status = contactEmailStatus;

    return searchParams;
  };

  // Fetch all Apollo IDs by paginating through search results
  const fetchAllApolloIds = async (token: string): Promise<string[]> => {
    const allIds: string[] = [];
    const searchParams = buildSearchParams();
    let page = 1;
    const maxPages = 100; // Safety limit

    while (page <= maxPages) {
      const response = await axios.post(
        `${hostname}/lists/${listId}/apollo/search`,
        {
          companyId,
          searchParams: { ...searchParams, page, per_page: 100 },
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const people = response.data.people || [];
      if (people.length === 0) break;

      allIds.push(...people.map((p: ApolloPerson) => p.id));

      const total = response.data.pagination?.total_entries || 0;
      if (allIds.length >= total) break;

      page++;
    }

    return allIds;
  };

  // Auto-save search with a generated name based on filters
  const autoSaveSearch = async (token: string) => {
    try {
      const searchParams = buildSearchParams();

      // Generate a name based on the filters
      const nameParts: string[] = [];
      if (personTitles.length > 0) {
        nameParts.push(personTitles.slice(0, 2).join(", "));
      }
      if (personSeniorities.length > 0) {
        const seniorityLabels = personSeniorities
          .map((s) => seniorityOptions.find((opt) => opt.value === s)?.label || s)
          .slice(0, 2);
        nameParts.push(seniorityLabels.join(", "));
      }
      if (organizationName.trim()) {
        nameParts.push(organizationName.trim());
      }
      if (organizationKeywordTags.length > 0) {
        nameParts.push(organizationKeywordTags.slice(0, 2).join(", "));
      }
      if (personLocations.length > 0) {
        nameParts.push(personLocations[0]);
      } else if (organizationLocations.length > 0) {
        nameParts.push(organizationLocations[0]);
      }

      // Create a descriptive name or use a default
      let searchName = nameParts.length > 0
        ? nameParts.join(" - ").slice(0, 100)
        : `Apollo Search ${new Date().toLocaleDateString()}`;

      await axios.post(
        `${hostname}/apollo/saved-searches`,
        {
          companyId,
          name: searchName,
          searchParams,
          lastResultCount: totalEntries,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
    } catch (err) {
      // Silently fail - don't interrupt the import flow
      console.error("Failed to auto-save search:", err);
    }
  };

  const handleImport = async (importAll: boolean) => {
    setImportLoading(true);
    setStatusMessage(null);

    try {
      const token = await getToken();

      let apolloIds: string[];

      if (importAll) {
        // Fetch all Apollo IDs from search results
        setStatusMessage({
          type: "info",
          message: `Fetching all ${totalEntries.toLocaleString()} records...`,
        });
        apolloIds = await fetchAllApolloIds(token);
      } else {
        // Use selected IDs
        apolloIds = Array.from(selectedIds);
      }

      if (apolloIds.length === 0) {
        setStatusMessage({
          type: "warning",
          message: "No people selected for import.",
        });
        return;
      }

      setStatusMessage({
        type: "info",
        message: `Importing ${apolloIds.length.toLocaleString()} people...`,
      });

      const response = await axios.post(
        `${hostname}/lists/${listId}/apollo/import`,
        {
          companyId,
          apolloIds,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Auto-save the search after successful import
      await autoSaveSearch(token);

      if (response.data.status === "background") {
        eventListener({
          type: "SET_SUCCESS",
          message:
            response.data.message ||
            `Import started in background for ${response.data.total || apolloIds.length} records`,
        });

        if (onBackgroundTaskCreated) {
          onBackgroundTaskCreated();
        }
      } else {
        eventListener({
          type: "SET_SUCCESS",
          message: `Successfully imported ${response.data.imported || 0} people`,
        });
      }

      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      setStatusMessage({
        type: "error",
        message: parseError(err) || "Failed to import from Apollo.",
      });
    } finally {
      setImportLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedIds.size === people.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(people.map((p) => p.id)));
    }
  };

  const handleToggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const hasAnyFilter = () => {
    return (
      personTitles.length > 0 ||
      personLocations.length > 0 ||
      personSeniorities.length > 0 ||
      personDepartments.length > 0 ||
      organizationLocations.length > 0 ||
      organizationDomains.length > 0 ||
      organizationName.trim() !== "" ||
      organizationNumEmployeesRanges.length > 0 ||
      organizationRevenueRanges.length > 0 ||
      organizationKeywordTags.length > 0 ||
      qKeywords.trim() !== "" ||
      contactEmailStatus.length > 0
    );
  };

  // Pagination helpers
  const totalPages = Math.ceil(totalEntries / perPage);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      handleSearch(newPage, true); // preserve selections when changing pages
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} fullWidth fullScreen>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <ApolloIcon color="primary" />
          <Typography variant="h6">Apollo People Search</Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ display: "flex", height: "100%" }}>
          {/* Left Panel - Filters */}
          <Box
            sx={{
              width: 380,
              minWidth: 380,
              borderRight: 1,
              borderColor: "divider",
              overflowY: "auto",
              p: 2,
            }}
          >
            {/* Saved Lists Section */}
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                  mb: 1,
                }}
                onClick={() => setSavedListsExpanded(!savedListsExpanded)}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Bookmark fontSize="small" color="primary" />
                  <Typography variant="subtitle1" fontWeight="bold">
                    Saved Lists
                  </Typography>
                  {savedSearches.length > 0 && (
                    <Chip
                      label={savedSearches.length}
                      size="small"
                      color="primary"
                      sx={{ height: 20, fontSize: "0.75rem" }}
                    />
                  )}
                </Box>
                <IconButton size="small">
                  {savedListsExpanded ? <NavigateBefore sx={{ transform: "rotate(-90deg)" }} /> : <NavigateBefore sx={{ transform: "rotate(90deg)" }} />}
                </IconButton>
              </Box>

              <Collapse in={savedListsExpanded}>
                {savedSearchesLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : savedSearches.length === 0 ? (
                  <Paper variant="outlined" sx={{ p: 2, textAlign: "center" }}>
                    <BookmarkBorder sx={{ fontSize: 32, color: "text.secondary", mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      No saved searches yet
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Run a search and save it for quick access
                    </Typography>
                  </Paper>
                ) : (
                  <Paper variant="outlined" sx={{ maxHeight: 200, overflowY: "auto" }}>
                    <List dense disablePadding>
                      {savedSearches.map((search) => (
                        <ListItem
                          key={search.id}
                          disablePadding
                          secondaryAction={
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={(e) => handleDeleteSavedSearch(search.id, e)}
                              sx={{ opacity: 0.6, "&:hover": { opacity: 1 } }}
                            >
                              <Close fontSize="small" />
                            </IconButton>
                          }
                        >
                          <ListItemButton onClick={() => handleLoadSavedSearch(search)}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <Bookmark fontSize="small" color="primary" />
                            </ListItemIcon>
                            <ListItemText
                              primary={search.name}
                              secondary={
                                search.lastResultCount
                                  ? `${parseInt(search.lastResultCount).toLocaleString()} results`
                                  : undefined
                              }
                              primaryTypographyProps={{ variant: "body2", noWrap: true }}
                              secondaryTypographyProps={{ variant: "caption" }}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                )}
              </Collapse>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Search Filters
            </Typography>

            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Apollo search is free and does not consume credits. Importing
                people to your list will use enrichment credits.
              </Typography>
            </Alert>

            {/* Person Filters Section */}
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mt: 2, mb: 1 }}
            >
              Person Filters
            </Typography>

            {/* Job Titles */}
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Job Titles"
                placeholder="Press Enter to add"
                value={personTitleInput}
                onChange={(e) => setPersonTitleInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddChip(
                      personTitleInput,
                      setPersonTitles,
                      setPersonTitleInput
                    );
                  }
                }}
                helperText="e.g., CEO, VP Sales, Software Engineer"
              />
              <Stack
                direction="row"
                spacing={0.5}
                sx={{ mt: 0.5, flexWrap: "wrap", gap: 0.5 }}
              >
                {personTitles.map((title, idx) => (
                  <Chip
                    key={idx}
                    label={title}
                    size="small"
                    onDelete={() => handleDeleteChip(idx, setPersonTitles)}
                  />
                ))}
              </Stack>
            </Box>

            {/* Person Locations */}
            <Autocomplete
              multiple
              freeSolo
              size="small"
              options={commonLocations}
              value={personLocations}
              onChange={(_, newValue) => setPersonLocations(newValue)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={index}
                    label={option}
                    size="small"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Person Locations"
                  placeholder="Type or select"
                  helperText="e.g., California, US"
                />
              )}
              sx={{ mb: 2 }}
            />

            {/* Seniority */}
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Seniority</InputLabel>
              <Select
                multiple
                value={personSeniorities}
                label="Seniority"
                onChange={(e: SelectChangeEvent<string[]>) =>
                  setPersonSeniorities(e.target.value as string[])
                }
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={
                          seniorityOptions.find((s) => s.value === value)
                            ?.label || value
                        }
                        size="small"
                      />
                    ))}
                  </Box>
                )}
              >
                {seniorityOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Department */}
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Department</InputLabel>
              <Select
                multiple
                value={personDepartments}
                label="Department"
                onChange={(e: SelectChangeEvent<string[]>) =>
                  setPersonDepartments(e.target.value as string[])
                }
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={
                          departmentOptions.find((d) => d.value === value)
                            ?.label || value
                        }
                        size="small"
                      />
                    ))}
                  </Box>
                )}
              >
                {departmentOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Divider sx={{ my: 2 }} />

            {/* Organization Filters Section */}
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              Organization Filters
            </Typography>

            {/* Organization Name */}
            <TextField
              fullWidth
              size="small"
              label="Company Name"
              value={organizationName}
              onChange={(e) => setOrganizationName(e.target.value)}
              helperText="Search by company name"
              sx={{ mb: 2 }}
            />

            {/* Organization Domains */}
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Company Domains"
                placeholder="Press Enter to add"
                value={organizationDomainInput}
                onChange={(e) => setOrganizationDomainInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddChip(
                      organizationDomainInput,
                      setOrganizationDomains,
                      setOrganizationDomainInput
                    );
                  }
                }}
                helperText="e.g., google.com, microsoft.com"
              />
              <Stack
                direction="row"
                spacing={0.5}
                sx={{ mt: 0.5, flexWrap: "wrap", gap: 0.5 }}
              >
                {organizationDomains.map((domain, idx) => (
                  <Chip
                    key={idx}
                    label={domain}
                    size="small"
                    onDelete={() =>
                      handleDeleteChip(idx, setOrganizationDomains)
                    }
                  />
                ))}
              </Stack>
            </Box>

            {/* Organization Locations */}
            <Autocomplete
              multiple
              freeSolo
              size="small"
              options={commonLocations}
              value={organizationLocations}
              onChange={(_, newValue) => setOrganizationLocations(newValue)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={index}
                    label={option}
                    size="small"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Company HQ Locations"
                  placeholder="Type or select"
                />
              )}
              sx={{ mb: 2 }}
            />

            {/* Employee Count */}
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Company Size</InputLabel>
              <Select
                multiple
                value={organizationNumEmployeesRanges}
                label="Company Size"
                onChange={(e: SelectChangeEvent<string[]>) =>
                  setOrganizationNumEmployeesRanges(e.target.value as string[])
                }
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={
                          employeeRangeOptions.find((r) => r.value === value)
                            ?.label || value
                        }
                        size="small"
                      />
                    ))}
                  </Box>
                )}
              >
                {employeeRangeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label} employees
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Revenue Range */}
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Company Revenue</InputLabel>
              <Select
                multiple
                value={organizationRevenueRanges}
                label="Company Revenue"
                onChange={(e: SelectChangeEvent<string[]>) =>
                  setOrganizationRevenueRanges(e.target.value as string[])
                }
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={
                          revenueRangeOptions.find((r) => r.value === value)
                            ?.label || value
                        }
                        size="small"
                      />
                    ))}
                  </Box>
                )}
              >
                {revenueRangeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Industry Keywords */}
            <Autocomplete
              multiple
              freeSolo
              size="small"
              options={industryOptions}
              value={organizationKeywordTags}
              onChange={(_, newValue) => setOrganizationKeywordTags(newValue)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={index}
                    label={option}
                    size="small"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Industry / Keywords"
                  placeholder="Type or select"
                />
              )}
              sx={{ mb: 2 }}
            />

            <Divider sx={{ my: 2 }} />

            {/* Other Filters */}
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              Additional Filters
            </Typography>

            {/* Keywords Search */}
            <TextField
              fullWidth
              size="small"
              label="Keywords"
              value={qKeywords}
              onChange={(e) => setQKeywords(e.target.value)}
              helperText="Full text search across profiles"
              sx={{ mb: 2 }}
            />

            {/* Email Status */}
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Email Status</InputLabel>
              <Select
                multiple
                value={contactEmailStatus}
                label="Email Status"
                onChange={(e: SelectChangeEvent<string[]>) =>
                  setContactEmailStatus(e.target.value as string[])
                }
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={
                          emailStatusOptions.find((s) => s.value === value)
                            ?.label || value
                        }
                        size="small"
                      />
                    ))}
                  </Box>
                )}
              >
                {emailStatusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Results per page */}
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Results per page</InputLabel>
              <Select
                value={perPage}
                label="Results per page"
                onChange={(e) => setPerPage(Number(e.target.value))}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>

            {/* Search Button */}
            <LoadingButton
              variant="contained"
              fullWidth
              onClick={() => handleSearch(1, false)}
              loading={searchLoading}
              startIcon={<Search />}
              disabled={!hasAnyFilter()}
            >
              Search
            </LoadingButton>

            {!hasAnyFilter() && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block", textAlign: "center" }}
              >
                Add at least one filter to search
              </Typography>
            )}

            {/* Save Search Section - shown after search */}
            {hasSearched && hasAnyFilter() && (
              <Box sx={{ mt: 2 }}>
                <Divider sx={{ mb: 2 }} />
                {showSaveDialog ? (
                  <Box>
                    <TextField
                      fullWidth
                      size="small"
                      label="Search Name"
                      value={saveSearchName}
                      onChange={(e) => setSaveSearchName(e.target.value)}
                      placeholder="e.g., Tech VPs in California"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && saveSearchName.trim()) {
                          handleSaveSearch();
                        } else if (e.key === "Escape") {
                          setShowSaveDialog(false);
                          setSaveSearchName("");
                        }
                      }}
                      sx={{ mb: 1 }}
                    />
                    <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                      <Button
                        size="small"
                        onClick={() => {
                          setShowSaveDialog(false);
                          setSaveSearchName("");
                        }}
                      >
                        Cancel
                      </Button>
                      <LoadingButton
                        variant="contained"
                        size="small"
                        onClick={handleSaveSearch}
                        loading={savingSearch}
                        disabled={!saveSearchName.trim()}
                        startIcon={<Bookmark />}
                      >
                        Save
                      </LoadingButton>
                    </Box>
                  </Box>
                ) : (
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<BookmarkBorder />}
                    onClick={() => setShowSaveDialog(true)}
                  >
                    Save This Search
                  </Button>
                )}
              </Box>
            )}
          </Box>

          {/* Right Panel - Results */}
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", p: 2 }}>
            {statusMessage && (
              <Alert severity={statusMessage.type} sx={{ mb: 2 }}>
                {statusMessage.message}
              </Alert>
            )}

            {!hasSearched ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: 1,
                  color: "text.secondary",
                }}
              >
                <PersonSearch sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6">Search for People</Typography>
                <Typography variant="body2">
                  Configure filters and click Search to find people
                </Typography>
              </Box>
            ) : people.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: 1,
                  color: "text.secondary",
                }}
              >
                <Typography variant="h6">No Results</Typography>
                <Typography variant="body2">
                  Try adjusting your search filters
                </Typography>
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle1">
                    {totalEntries.toLocaleString()} people found
                    {selectedIds.size > 0 &&
                      ` (${selectedIds.size} selected)`}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <LoadingButton
                      variant="outlined"
                      size="small"
                      onClick={() => handleImport(false)}
                      loading={importLoading}
                      disabled={selectedIds.size === 0}
                      startIcon={<Check />}
                    >
                      Import Selected ({selectedIds.size})
                    </LoadingButton>
                    <LoadingButton
                      variant="contained"
                      size="small"
                      onClick={() => handleImport(true)}
                      loading={importLoading}
                      startIcon={<Check />}
                    >
                      Import All ({totalEntries.toLocaleString()})
                    </LoadingButton>
                  </Box>
                </Box>

                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ flex: 1 }}
                >
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            indeterminate={
                              selectedIds.size > 0 &&
                              selectedIds.size < people.length
                            }
                            checked={
                              people.length > 0 &&
                              selectedIds.size === people.length
                            }
                            onChange={handleSelectAll}
                          />
                        </TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Title</TableCell>
                        <TableCell>Company</TableCell>
                        <TableCell>Location</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Phone</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {people.map((person) => (
                        <TableRow
                          key={person.id}
                          hover
                          selected={selectedIds.has(person.id)}
                          onClick={() => handleToggleSelect(person.id)}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox checked={selectedIds.has(person.id)} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {person.first_name}{" "}
                              {person.last_name ||
                                person.last_name_obfuscated ||
                                ""}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                maxWidth: 200,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {person.title}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                            >
                              <Business
                                sx={{ fontSize: 16, color: "text.secondary" }}
                              />
                              <Typography variant="body2">
                                {person.organization?.name || "-"}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {person.has_city || person.has_state || person.has_country ? (
                              <Chip
                                label="Available"
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {person.has_email ? (
                              <Chip
                                label="Available"
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {person.has_direct_phone === "Yes" ? (
                              <Chip
                                label="Available"
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            ) : person.has_direct_phone?.includes("Maybe") ? (
                              <Tooltip title={person.has_direct_phone}>
                                <Chip
                                  label="Maybe"
                                  size="small"
                                  variant="outlined"
                                />
                              </Tooltip>
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                -
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination Controls */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: 2,
                    pt: 2,
                    borderTop: 1,
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Showing {((currentPage - 1) * perPage) + 1}-{Math.min(currentPage * perPage, totalEntries)} of{" "}
                    {totalEntries.toLocaleString()} results
                    {selectedIds.size > 0 && ` (${selectedIds.size} selected across pages)`}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1 || searchLoading}
                    >
                      <FirstPage />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1 || searchLoading}
                    >
                      <NavigateBefore />
                    </IconButton>
                    <Typography variant="body2" sx={{ mx: 1 }}>
                      Page {currentPage} of {totalPages.toLocaleString()}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages || searchLoading}
                    >
                      <NavigateNext />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages || searchLoading}
                    >
                      <LastPage />
                    </IconButton>
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <LoadingButton onClick={onClose} disabled={searchLoading || importLoading}>
          Cancel
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ApolloSearchDialog;
